import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollReveal } from '@/hooks/useScrollReveal';

const csrPillars = [
  {
    icon: 'ri-heart-pulse-line',
    title: 'Community Upliftment',
    description:
      'Supporting underserved communities through skill development and employment readiness programs. We partner with local organizations to create pathways to meaningful work.',
    image: 'https://readdy.ai/api/search-image?query=Volunteer%20team%20helping%20young%20adults%20with%20job%20interview%20preparation%20and%20resume%20writing%20in%20a%20bright%20community%20center%2C%20warm%20natural%20lighting%2C%20diverse%20group%20of%20people%2C%20candid%20documentary%20style%20photography%2C%20hopeful%20expressions%2C%20clean%20modern%20interior%20with%20plants&width=800&height=600&seq=csr-community-01&orientation=landscape',
  },
  {
    icon: 'ri-seedling-line',
    title: 'Sustainability',
    description:
      'Promoting environmentally conscious practices in every aspect of how we operate and grow. From paperless recruitment to green office initiatives, we walk the talk.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20eco-friendly%20office%20workspace%20with%20lush%20green%20plants%20and%20natural%20wood%20furniture%2C%20employees%20working%20on%20laptops%20in%20a%20sustainable%20environment%2C%20large%20windows%20with%20sunlight%20streaming%20in%2C%20minimalist%20green%20interior%20design%2C%20clean%20air%20quality%20concept&width=800&height=600&seq=csr-sustainability-01&orientation=landscape',
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Social Responsibility',
    description:
      'Partnering with NGOs and social enterprises to create lasting positive change beyond the workplace. We believe business success and social good go hand in hand.',
    image: 'https://readdy.ai/api/search-image?query=Corporate%20team%20volunteering%20at%20a%20community%20food%20drive%20or%20education%20event%2C%20people%20wearing%20casual%20branded%20t-shirts%20distributing%20supplies%2C%20warm%20golden%20hour%20lighting%2C%20genuine%20smiles%20and%20collaboration%2C%20outdoor%20community%20setting%2C%20documentary%20photography%20style&width=800&height=600&seq=csr-social-01&orientation=landscape',
  },
];

const impactStats = [
  { value: '500+', label: 'Lives Impacted' },
  { value: '12+', label: 'Partner NGOs' },
  { value: '8+', label: 'Years of Giving' },
];

export default function CSRPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollReveal(0.1);
  const { ref: pillarsRef, isVisible: pillarsVisible } = useScrollReveal(0.05);
  const { ref: impactRef, isVisible: impactVisible } = useScrollReveal(0.1);
  const { ref: galleryRef, isVisible: galleryVisible } = useScrollReveal(0.1);

  return (
    <main>
      <Navbar />
      <div className="h-20"></div>

      {/* Hero */}
      <section
        className="relative py-20 md:py-28 px-6 md:px-10 lg:px-16 overflow-hidden"
        style={{ backgroundColor: '#0b1f14' }}
      >
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-accent-500/8 blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] rounded-full bg-primary-500/10 blur-3xl pointer-events-none"></div>

        <div
          ref={heroRef}
          className={`relative max-w-4xl mx-auto text-center transition-all duration-700 ${
            heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span
            className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-6"
            style={{ backgroundColor: '#1a3d24', color: '#c8dfc2' }}
          >
            CSR Initiatives
          </span>

          <h1
            className="font-heading text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6"
            style={{ color: '#ffffff' }}
          >
            CSR at{' '}
            <span style={{ color: '#c8dfc2' }} className="italic">Our Core</span>
          </h1>

          <p
            className="text-lg md:text-xl leading-relaxed max-w-2xl mx-auto"
            style={{ color: '#d4e6d0' }}
          >
            Beyond business — we actively work toward meaningful social impact through our CSR initiatives. At Job Manifest, responsibility is not an afterthought. It is woven into everything we do.
          </p>
        </div>
      </section>

      {/* Impact Stats */}
      <section
        className="relative py-16 md:py-20 px-6 md:px-10 lg:px-16"
        style={{ backgroundColor: '#0f291a' }}
      >
        <div
          ref={impactRef}
          className={`max-w-5xl mx-auto transition-all duration-700 ${
            impactVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-12">
            {impactStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div
                  className="font-heading text-5xl md:text-6xl font-extrabold mb-2"
                  style={{ color: '#c8dfc2' }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-sm font-medium tracking-wide uppercase"
                  style={{ color: '#d4e6d0' }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CSR Pillars with Images */}
      <section
        className="relative py-20 md:py-28 px-6 md:px-10 lg:px-16"
        style={{ backgroundColor: '#0b1f14' }}
      >
        <div
          ref={pillarsRef}
          className={`max-w-6xl mx-auto transition-all duration-700 ${
            pillarsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex flex-col gap-16 md:gap-24">
            {csrPillars.map((pillar, idx) => (
              <div
                key={pillar.title}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center ${
                  idx % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={`${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                    <img
                      src={pillar.image}
                      alt={pillar.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={`${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div
                    className="w-14 h-14 flex items-center justify-center rounded-2xl mb-6"
                    style={{ backgroundColor: '#1a3d24' }}
                  >
                    <i className={`${pillar.icon} text-xl`} style={{ color: '#c8dfc2' }}></i>
                  </div>

                  <h3
                    className="font-heading text-2xl md:text-3xl font-bold mb-4"
                    style={{ color: '#ffffff' }}
                  >
                    {pillar.title}
                  </h3>

                  <p
                    className="text-base md:text-lg leading-relaxed"
                    style={{ color: '#d4e6d0' }}
                  >
                    {pillar.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Gallery */}
      <section
        className="relative py-20 md:py-28 px-6 md:px-10 lg:px-16"
        style={{ backgroundColor: '#0b1f14' }}
      >
        <div
          ref={galleryRef}
          className={`max-w-6xl mx-auto transition-all duration-700 ${
            galleryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="text-center mb-12 md:mb-16">
            <span
              className="inline-block px-4 py-1.5 rounded-full text-xs font-bold tracking-widest uppercase mb-5"
              style={{ backgroundColor: '#1a3d24', color: '#c8dfc2' }}
            >
              Impact in Action
            </span>
            <h2
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
              style={{ color: '#ffffff' }}
            >
              Our Work in the <span style={{ color: '#c8dfc2' }} className="italic">Community</span>
            </h2>
            <p
              className="text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
              style={{ color: '#d4e6d0' }}
            >
              Real moments from our CSR outreach programs. Every smile, every meal shared, and every lesson taught is a step toward building a better tomorrow.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://static.readdy.ai/image/c7c2c42d06e77c2512a8742db199c6e1/3931bd4f3f5e8056e676fe44a4c9ebc2.png"
                alt="CSR community outreach - volunteers distributing supplies to children"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://static.readdy.ai/image/c7c2c42d06e77c2512a8742db199c6e1/57095997d6d35685c15c7e416233b07f.png"
                alt="CSR education support - children participating in learning activities"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://static.readdy.ai/image/c7c2c42d06e77c2512a8742db199c6e1/187ec296e6c1bfc185fc90b694469527.png"
                alt="CSR community gathering - volunteers with children in a group session"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3]">
              <img
                src="https://static.readdy.ai/image/c7c2c42d06e77c2512a8742db199c6e1/c9963e5cbc216a7685138b004b5cc29f.png"
                alt="CSR food distribution - volunteers distributing meals to children"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section
        className="relative py-16 md:py-20 px-6 md:px-10 lg:px-16"
        style={{ backgroundColor: '#0f291a' }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h3
            className="font-heading text-2xl md:text-4xl font-bold mb-4"
            style={{ color: '#ffffff' }}
          >
            Want to Partner With Us?
          </h3>
          <p
            className="text-base md:text-lg mb-8 max-w-xl mx-auto"
            style={{ color: '#d4e6d0' }}
          >
            If your organization shares our vision for ethical, people-first change, we would love to collaborate.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-300 cursor-pointer"
            style={{ backgroundColor: '#c8dfc2', color: '#1a3d24' }}
          >
            Get In Touch
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-up-line text-sm"></i>
            </span>
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}